import React, { memo, useCallback, useMemo, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { AssetItemDto } from '@api/Api';
import Loader from '@components/Loader';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import {
  toggleApprovalAssetModal,
  toggleApprovalModal
} from '@redux/actions/modalAction';
import { useCurrentWorkspace } from '@hooks/workspace';

type AssetItemProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  asset: AssetItemDto;
  // eslint-disable-next-line react/no-unused-prop-types
  onCloseModal: () => void;
  // eslint-disable-next-line react/no-unused-prop-types
  refetchApprovalsData: () => void;
};

export default memo(
  forwardRef<HTMLDivElement, AssetItemProps>(function AssetItem(
    { asset, onCloseModal, refetchApprovalsData },
    ref
  ) {
    const dispatch = useDispatch();
    const [currentWorkspace] = useCurrentWorkspace(false);
    const canCreateAw = !!currentWorkspace?.permissions.createApprovalWorkflows;
    const canManageAW = !!asset.asset?.approvalWorkflow?.permissions.manage;
    const assetFirstVersion = useMemo(
      () => asset.asset?.versions[0],
      [asset.asset?.versions]
    );
    const assetImage = useMemo(() => {
      if (!assetFirstVersion) return 'pending';
      return assetPreviewThumbnails({
        url: assetFirstVersion.metadata.preview?.url || '',
        status: assetFirstVersion.metadata.preview?.status,
        type: assetFirstVersion.metadata.type,
        format: 'img'
      });
    }, [assetFirstVersion]);

    const fileSize = useMemo(() => {
      const sizes: string[] = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
      let fs = 'n/a';
      if (!assetFirstVersion) return fs;
      if (assetFirstVersion.size > 0) {
        const i: number = parseInt(
          Math.floor(
            Math.log(assetFirstVersion.size) / Math.log(1024)
          ).toString(),
          10
        );
        if (i === 0) fs = `${assetFirstVersion.size} ${sizes[i]}`;
        fs = `${(assetFirstVersion.size / 1024 ** i).toFixed(1)} ${sizes[i]}`;
      }
      return fs;
    }, [assetFirstVersion]);
    const onOpenApprovalModal = useCallback(() => {
      if (!assetFirstVersion) return;
      if (!currentWorkspace?.id) return;
      onCloseModal();
      if (asset.asset?.approvalWorkflow) {
        dispatch(
          toggleApprovalAssetModal({
            assetId: asset.id,
            visible: true,
            versionId: asset.asset.versions[0].id,
            approvalWorkflowId: asset.asset?.approvalWorkflow.id,
            callback: () => {
              refetchApprovalsData();
            }
          })
        );
        return;
      }
      dispatch(
        toggleApprovalModal({
          visible: true,
          approvalWorkflowId: asset.asset?.approvalWorkflow?.id,
          assetVersions: [
            {
              ...assetFirstVersion,
              assetId: asset.id
            }
          ],
          permissions: {
            create: canCreateAw
          },
          campaignId: asset.campaignId,
          callback: () => {
            refetchApprovalsData();
          }
        })
      );
    }, [
      canManageAW,
      assetFirstVersion,
      canCreateAw,
      onCloseModal,
      dispatch,
      refetchApprovalsData,
      asset.campaignId,
      asset.asset?.approvalWorkflow?.id
    ]);

    return (
      <div ref={ref} className="asset-list--item" onClick={onOpenApprovalModal}>
        <div>
          <div className="preview">
            {assetImage === 'pending' && (
              <Loader spinSize={40} isSpinning={true} />
            )}
            {assetImage !== 'pending' && typeof assetImage === 'string' && (
              <img src={`${assetImage}`} alt="" />
            )}
          </div>
          <div className="name">
            <span>{assetFirstVersion?.name}</span>
            <div className="ext">{assetFirstVersion?.extension}</div>
          </div>
        </div>
        <div>
          <div className="size">{fileSize}</div>
        </div>
      </div>
    );
  })
);
